import { type DocumentNode, Kind, type SelectionSetNode } from 'graphql';

import { isOtherCompositionBucket } from '@revelio/core/composition-buckets';
import {
  type CompositionV3BreakdownOptions,
  type CompositionV3Corrections,
  type CompositionV3DataWithActivitiesQuery,
  type CompositionV3Req,
  type CompositionV3RespData,
  type CompositionV4BreakdownReq,
  type CompositionV4Corrections,
  CompositionV4Dimension,
  type CompositionV4QueryVariables,
  EntKind,
  WorkforceType,
} from '@revelio/data-access';

import type { CompositionV4Metric } from '../PageConfigProvider/types/metric';
import { MAX_COMPOSITION_V4_BREAKDOWNS } from './composition-v4-tables';
import {
  type CompositionV4Response,
  compositionV4MetricSelection,
} from './use-composition-v4-query';

type CompositionV3Breakdown = Exclude<
  keyof Omit<CompositionV3RespData, '__typename'>,
  CompositionV4Metric
>;

type BreakdownOptionKey = keyof CompositionV3BreakdownOptions;
type LevelKey = Extract<BreakdownOptionKey, `${string}_level`>;
type TopNKey = Extract<BreakdownOptionKey, `${string}_top_n`>;

const BREAKDOWNS: Record<
  CompositionV3Breakdown,
  {
    dimension: CompositionV4Dimension;
    level?: LevelKey;
    topN: TopNKey;
    ids: Exclude<BreakdownOptionKey, LevelKey | TopNKey>;
  }
> = {
  gender: {
    dimension: CompositionV4Dimension.Gender,
    topN: 'gender_top_n',
    ids: 'gender',
  },
  ethnicity: {
    dimension: CompositionV4Dimension.Ethnicity,
    topN: 'ethnicity_top_n',
    ids: 'ethnicity',
  },
  role: {
    dimension: CompositionV4Dimension.Role,
    level: 'role_breakdown_level',
    topN: 'role_top_n',
    ids: 'role',
  },
  geography: {
    dimension: CompositionV4Dimension.Geography,
    level: 'geo_breakdown_level',
    topN: 'geography_top_n',
    ids: 'geography',
  },
  seniority: {
    dimension: CompositionV4Dimension.Seniority,
    topN: 'seniority_top_n',
    ids: 'seniority',
  },
  education: {
    dimension: CompositionV4Dimension.Education,
    topN: 'education_top_n',
    ids: 'education',
  },
  rics: {
    dimension: CompositionV4Dimension.Rics,
    level: 'rics_breakdown_level',
    topN: 'rics_top_n',
    ids: 'rics',
  },
  skills: {
    dimension: CompositionV4Dimension.Skill,
    level: 'skill_breakdown_level',
    topN: 'skill_top_n',
    ids: 'skill',
  },
  activities: {
    dimension: CompositionV4Dimension.Activity,
    level: 'activity_breakdown_level',
    topN: 'activities_top_n',
    ids: 'activities',
  },
};

const METRICS: readonly CompositionV4Metric[] = [
  'headcount',
  'tenure',
  'salary',
  'growth_rate',
  'hiring_rate',
  'attrition_rate',
];

const isMetric = (field: string): field is CompositionV4Metric =>
  METRICS.includes(field as CompositionV4Metric);

const isBreakdown = (field: string): field is CompositionV3Breakdown =>
  field in BREAKDOWNS;

export type CompositionV3Selection = {
  metrics: readonly CompositionV4Metric[];
  breakdowns: readonly CompositionV3Breakdown[];
};

const fieldSelectionSet = (
  selectionSet: SelectionSetNode | undefined,
  name: string
): SelectionSetNode | undefined => {
  const field = selectionSet?.selections.find(
    (selection) =>
      selection.kind === Kind.FIELD && selection.name.value === name
  );
  return field?.kind === Kind.FIELD ? field.selectionSet : undefined;
};

export const getCompositionV3Selection = (
  document: DocumentNode
): CompositionV3Selection => {
  const operation = document.definitions.find(
    (definition) => definition.kind === Kind.OPERATION_DEFINITION
  );
  const metrics = fieldSelectionSet(
    fieldSelectionSet(
      fieldSelectionSet(
        operation?.kind === Kind.OPERATION_DEFINITION
          ? operation.selectionSet
          : undefined,
        'composition_v3'
      ),
      'entities'
    ),
    'metrics'
  );
  const fields = (metrics?.selections ?? []).flatMap((selection) =>
    selection.kind === Kind.FIELD ? [selection.name.value] : []
  );
  return {
    metrics: fields.filter(isMetric),
    breakdowns: fields.filter(isBreakdown),
  };
};

const toCompositionV4Corrections = ({
  with_weight,
  with_sample_weight,
  workforce_type,
  with_contingent_workers,
}: CompositionV3Corrections): CompositionV4Corrections => ({
  with_weight: with_weight === true,
  with_sample_weight: with_sample_weight === true,
  workforce_type:
    workforce_type ??
    (with_contingent_workers ? WorkforceType.Complete : WorkforceType.FullTime),
});

const toCompositionV4Breakdown = (
  breakdown: CompositionV3Breakdown,
  options: CompositionV3BreakdownOptions
): CompositionV4BreakdownReq => {
  const { dimension, level, topN, ids } = BREAKDOWNS[breakdown];
  const topNValue = options[topN];
  const idsValue = options[ids];
  return {
    dimension,
    ...(level ? { level: options[level] ?? EntKind.ActivityLvl_1 } : {}),
    ...(topNValue && topNValue > 0 ? { top_n: topNValue } : {}),
    ...(idsValue?.length ? { ids: idsValue } : {}),
  };
};

export const toCompositionV4Variables = (
  {
    entities,
    filters,
    corrections,
    role_taxonomy,
    skill_taxonomy,
    activity_taxonomy,
    breakdown_options,
    cache_options,
  }: CompositionV3Req,
  { metrics, breakdowns }: CompositionV3Selection
): {
  primary: CompositionV4QueryVariables;
  overflow?: CompositionV4QueryVariables;
} => {
  const requested = breakdowns.map((breakdown) =>
    toCompositionV4Breakdown(breakdown, breakdown_options)
  );
  const variables = (
    batch: readonly CompositionV4BreakdownReq[],
    batchMetrics: readonly CompositionV4Metric[]
  ): CompositionV4QueryVariables => ({
    req: {
      entities,
      filters,
      corrections: toCompositionV4Corrections(corrections),
      role_taxonomy,
      skill_taxonomy,
      activity_taxonomy,
      cache_options,
      ...(batch.length > 0 ? { breakdowns: [...batch] } : {}),
    },
    ...compositionV4MetricSelection(batchMetrics),
  });
  const overflow = requested.slice(MAX_COMPOSITION_V4_BREAKDOWNS);
  return {
    primary: variables(
      requested.slice(0, MAX_COMPOSITION_V4_BREAKDOWNS),
      metrics
    ),
    ...(overflow.length > 0 ? { overflow: variables(overflow, []) } : {}),
  };
};

type CompositionV3Entity = NonNullable<
  CompositionV3DataWithActivitiesQuery['composition_v3']
>['entities'][number];

type CompositionV3Metrics = CompositionV3Entity['metrics'];

export type CompositionV3QueryData = {
  composition_v3: {
    entities: (Omit<CompositionV3Entity, 'metrics'> & {
      index: number;
      metrics: Partial<CompositionV3Metrics>;
    })[];
  };
};

type CompositionV4Category =
  CompositionV4Response['entities'][number]['breakdowns'][number]['categories'][number];

const toCompositionV3Categories = (
  categories: readonly CompositionV4Category[]
): CompositionV3Metrics[CompositionV3Breakdown] =>
  categories.map(({ id, short_name, headcount }, index) => ({
    id,
    rank: isOtherCompositionBucket(id) ? -1 : index,
    short_name,
    long_name: short_name,
    headcount,
  }));

export const toCompositionV3Data = (
  [primary, ...overflow]: readonly [
    CompositionV4Response,
    ...CompositionV4Response[],
  ],
  { metrics, breakdowns }: CompositionV3Selection
): CompositionV3QueryData => ({
  composition_v3: {
    entities: primary.entities.map((entity) => {
      const categories = new Map(
        [
          entity,
          ...overflow.map(({ entities }) =>
            entities.find(({ index }) => index === entity.index)
          ),
        ]
          .flatMap((source) => source?.breakdowns ?? [])
          .map(({ dimension, categories }) => [dimension, categories])
      );
      const v3Metrics: Partial<CompositionV3Metrics> = {};
      for (const metric of metrics) {
        v3Metrics[metric] = entity.metrics[metric] ?? [];
      }
      for (const breakdown of breakdowns) {
        v3Metrics[breakdown] = toCompositionV3Categories(
          categories.get(BREAKDOWNS[breakdown].dimension) ?? []
        );
      }
      return {
        id: entity.id,
        index: entity.index,
        short_name: entity.short_name,
        long_name: entity.long_name,
        metrics: v3Metrics,
      };
    }),
  },
});
